import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import { useAuth } from "../components/authContext";
import "./MyRequest.css";
import { useRequireListing } from "../hooks/useRequireListing";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

function formatDateTimeRange(startIso, endIso, timeZone) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = new Date(startIso);
  const end = new Date(endIso);
  const datePart = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: tz });
  const timeOpts = { hour: "numeric", minute: "2-digit", timeZone: tz };
  const startPart = start.toLocaleTimeString([], timeOpts);
  const endPart = end.toLocaleTimeString([], timeOpts);
  return `${datePart} • ${startPart} – ${endPart}`;
}

function formatMoney(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function formatStatus(status) {
  return String(status || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatExtraCost(type) {
  return type === "paid_separately" ? "Paid Separately" : "Included";
}

export default function MyRequest() {
  const { id } = useParams();
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const { checking: checkingListing, allowed: hasListing } = useRequireListing();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [showDeclineBox, setShowDeclineBox] = useState(false);
  const [declineNote, setDeclineNote] = useState("");
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState("");

  useEffect(() => {
    if (!authReady) return;
    if (user === null) {
      navigate("/login", { replace: true, state: { from: `/request/${id}` } });
    }
  }, [user, authReady, navigate, id]);

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!authReady || !user || !hasListing) return;
      try {
        setLoading(true);
        setError("");
        const token = await user.getIdToken();
        const r = await fetch(`${API_BASE_URL}/api/my/requests/${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || "Failed to load request.");
        if (!alive) return;
        setData(j);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load request.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [user, authReady, id, hasListing]);

  const payoutsCta = useCallback(async (e) => {
    e?.preventDefault();
    if (!user || payoutsLoading) return;
    try {
      setPayoutsLoading(true);
      const idToken = await user.getIdToken();
      const r = await fetch(`${API_BASE_URL}/api/my/payouts`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json"
        }
      });
      const j = await r.json();
      if (!r.ok || !j?.url) throw new Error("Could not open Stripe. Please try again.");
      window.location.href = j.url;
    } catch (e) {
      setError(e.message || "Could not open Stripe. Please try again.");
      setPayoutsLoading(false);
    }
  }, [user, payoutsLoading]);

  const statusLabel = useMemo(() => formatStatus(data?.status), [data]);
  const firstName = useMemo(() => (data?.customerName || "").split(" ")[0] || "this guest", [data]);
  const hasBuddyReview = Boolean(data?.buddyReview);
  const canBuddyReview = data?.status === "completed" && !hasBuddyReview;

  const submitBuddyReview = async () => {
    if (!canBuddyReview) return;
    if (reviewRating < 1 || reviewRating > 5) {
      setReviewError("Please select a rating.");
      return;
    }
    if (reviewComment.length > 500) {
      setReviewError("Comment must be 500 characters or less.");
      return;
    }
    try {
      setReviewSaving(true);
      setReviewError("");
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/my/requests/${encodeURIComponent(id)}/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment })
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || "Could not submit review.");
      setData((prev) => ({ ...(prev || {}), buddyReview: { rating: reviewRating, comment: reviewComment, createdAt: new Date().toISOString() } }));
    } catch (e) {
      setReviewError(e.message || "Could not submit review.");
    } finally {
      setReviewSaving(false);
    }
  };

  const subheader = useMemo(() => {
    const s = data?.status;
    if (s === "accepted") return `Enjoy your activity with ${firstName}!`;
    if (s === "accepted_needs_payout") return (
      <>Enjoy your activity with {firstName}! You need to{" "}
        <span
          role="button"
          tabIndex={0}
          className="mr2-inline-link"
          onClick={payoutsCta}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && payoutsCta(e)}
        >
          set up Stripe
        </span>{" "}
        to get paid for this activity.</>
    );
    if (s === "completed") return "Thanks for hosting!";
    if (s === "declined" || s === "cancelled") return "You have declined this request.";
    return "Review and take action on this booking.";
  }, [data?.status, firstName, payoutsCta]);

  const decide = async (action, note = "") => {
    if (!user || !data || data.status !== "pending") return;
    const trimmedNote = (note || "").trim();
    if (trimmedNote.length > 500) {
      setError("Message is too long (max 500 characters).");
      return;
    }
    try {
      setSaving(true);
      const token = await user.getIdToken();
      const r = await fetch(`${API_BASE_URL}/api/my/requests/${encodeURIComponent(id)}/decision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, note: trimmedNote })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || "Failed to update request.");
      setError("");
      const fallback = action === "accept" ? "accepted" : "declined";
      const nextStatus = j.status || fallback;
      setData((prev) => ({
        ...(prev || {}),
        status: nextStatus,
        paymentStatus: j.paymentStatus || prev?.paymentStatus,
        retryPaymentUrl: j.retryPaymentUrl || prev?.retryPaymentUrl,
        lastPaymentErrorMessage: j.message || prev?.lastPaymentErrorMessage
      }));
      if (action === "decline") {
        setShowDeclineBox(false);
        setDeclineNote("");
      }
    } catch (e) {
      setError(e.message || "Failed to update request.");
    } finally {
      setSaving(false);
    }
  };

  if (!user || checkingListing) {
    return <div className="mr2-container"><div className="mr2-card">Loading…</div></div>;
  }

  if (loading) {
    return <div className="mr2-container"><div className="mr2-card">Loading…</div></div>;
  }

  if (error) {
    return (
      <div className="mr2-container">
        <div className="mr2-card">
          <div className="mr2-error">{error}</div>
          <button className="mr2-btn" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const isPending = data?.status === "pending";

  return (
    <div className="mr2-container">
      <div className="mr2-card">
        <button className="pv-back" onClick={() => navigate("/requests")} aria-label="Back to requests">
          <ArrowBackIos fontSize="small" />
          <span>Back</span>
        </button>
        <div className="mr2-head">
          <h1>Request Details</h1>
          <p>{subheader}</p>
          <div className="mr2-status">
            {isPending && <span className="mr2-dot" aria-hidden />}
            <span className={`mr2-status-label ${isPending ? "is-strong" : ""}`}>{statusLabel}</span>
          </div>
        </div>

              <div className="mr2-sections">
        
        {/* When / where */}
        <div className="mr2-section">
          <div className="mr2-section-title">When &amp; Where</div>
          <div className="mr2-row">
            <div className="mr2-label">Date &amp; Time</div>
            <div className="mr2-value">
              {formatDateTimeRange(data.startTime, data.endTime, data.hostTimeZone)}
            </div>
          </div>
          <div className="mr2-section-subdivider"></div>

          <div className="mr2-row">
            <div className="mr2-label">Location</div>
            <div className="mr2-value">{data.location || "—"}</div>
          </div>
        </div>
        {/* Customer / intro */}
        <div className="mr2-section">
          <div className="mr2-section-title">Customer</div>
          <div className="mr2-row">
            <div className="mr2-label">Name</div>
            <div className="mr2-value">
              {data.customerName || "—"}
            </div>
          </div>
          <div className="mr2-row">
            <div className="mr2-label">Intro Message</div>
            <div className="mr2-value">
              {data.intro || "None provided."}
            </div>
          </div>
        </div>

        {/* Activity details */}
        <div className="mr2-section">
          <div className="mr2-section-title">Activity</div>
          <div className="mr2-row">
            <div className="mr2-label">Title</div>
            <div className="mr2-value">
              {data.activityTitle || "—"}
            </div>
          </div>
          <div className="mr2-row">
            <div className="mr2-label">Description</div>
            <div className="mr2-value">
              {data.activityDescription || "—"}
            </div>
          </div>
                    <div className="mr2-row">
            <div className="mr2-label">Extra Cost</div>
            <div className="mr2-value">
              <div>
                <span className="mr2-strong">Type:</span>{" "}
                {formatExtraCost(data.extraCostType)}
              </div>
              {data.extraCostNote && (
                <div>
                  <span className="mr2-strong">Note:</span>{" "}
                  {data.extraCostNote}
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Money */}
        <div className="mr2-section">
          <div className="mr2-section-title">Payment</div>
          <div className="mr2-row">
            <div className="mr2-label">Your Earnings</div>
            <div className="mr2-value mr2-earnings">
              {formatMoney(data.earningsCents)}
            </div>
          </div>

        </div>
      </div>


        {isPending && (
          <>
            <div className="mr2-actions-box">
              <div className="mr2-actions">
                <button className="mr2-btn mr2-btn-primary mr2-btn-strong" onClick={() => decide("accept")} disabled={saving}>
                  {saving ? "Updating…" : "Accept"}
                </button>
                <button
                  className="mr2-btn mr2-btn-secondary mr2-btn-strong"
                onClick={() => {
                  setShowDeclineBox(true);
                  setError("");
                  setTimeout(() => {
                    const el = document.getElementById("mr2-decline-anchor");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 0);
                }}
                disabled={saving}
              >
                Decline
              </button>
            </div>
            </div>
          </>
        )}

        {isPending && showDeclineBox && (
          <div className="mr2-decline-box" id="mr2-decline-anchor">
            <textarea
              className="mr2-textarea"
              placeholder={`Leave an optional message to ${firstName} about why you're declining`}
              maxLength={500}
              value={declineNote}
              onChange={(e) => setDeclineNote(e.target.value)}
              disabled={saving}
            />
            <div className="mr2-charcount">{declineNote.length}/500</div>
            <div className="mr2-actions mr2-actions-vertical">
              <button
                className="mr2-btn mr2-btn-primary mr2-btn-full"
                onClick={() => decide("decline", declineNote)}
                disabled={saving}
              >
                {saving ? "Sending…" : "Confirm Decline"}
              </button>
              <button
                className="mr2-inline-link-button"
                onClick={() => {
                  setShowDeclineBox(false);
                  setDeclineNote("");
                  setError("");
                }}
                disabled={saving}
              >
                Keep Request
              </button>
            </div>
          </div>
        )}

        {(data.status === "accepted" || data.status === "accepted_needs_payout") && (
          <>
            <div className="mr2-note">You will be able to review on this page when the status is Completed.</div>
          </>
        )}
        {data.status === "completed" && (
          <div className="mr2-review-box">
            <div className="mr2-label mr2-review-title">Leave a Review</div>
            {data.buddyReview ? (
              <div className="mr2-softcard">
                <div className="mr2-review-stars">Rating: {data.buddyReview.rating}/5</div>
                {data.buddyReview.comment && <div className="mr2-text-plain mr2-review-comment">"{data.buddyReview.comment}"</div>}
              </div>
            ) : (
              <>
                <div className="mr2-review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`mr2-star ${reviewRating >= n ? "is-active" : ""}`}
                      onClick={() => setReviewRating(n)}
                      disabled={reviewSaving}
                      aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <div className="mr2-review-input-wrap">
                  <textarea
                    className="mr2-review-textarea"
                    placeholder="Share a short comment (optional)"
                    maxLength={500}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    disabled={reviewSaving}
                  />
                  <div className="mr2-review-charcount-inline">{reviewComment.length}/500</div>
                </div>
                <div className="mr2-review-actions">
                  <button className="mr2-review-btn" onClick={submitBuddyReview} disabled={reviewSaving}>
                    {reviewSaving ? "Submitting..." : "Submit Review"}
                  </button>
                </div>
                {reviewError && <div className="mr2-error">{reviewError}</div>}
              </>
            )}
          </div>
        )}
          <div className="mr2-note">If you have any questions or concerns, contact us at support@activitybuddy.com.</div>
      </div>
    </div>
  );
}
